import { Button, Divider, Switch } from "antd"
import { FC } from "react"
import { DividerVertical, EmployeeDutyShiftsWrapper } from "./WorkplaceCard"
import calenderDots from "assets/svg/CalendarDots.svg"
import alarmIcon from "assets/svg/alarm.svg"
import userIcon from "assets/svg/users.svg"
import powerIcon from "assets/svg/power.svg"
// ─── Types ───────────────────────────────────────────────────────────────────

export type ConfigurationCardPropsCardStatus = "published" | "draft" | boolean

interface ConfigurationCardProps {
  title: string
  date: string
  employeeCount: number
  shiftCount: number
  status?: ConfigurationCardPropsCardStatus
  isLoading?: boolean
  isRestoreLoading?: boolean
  onEdit?: () => void
  onArchive?: () => void
  onRestore?: () => void
  onToggle?: () => void
  onDelete?: () => void
  isDeletingShiftPlan?: boolean
  flaggedForDeletion?: boolean
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const isPublished = (status?: ConfigurationCardPropsCardStatus): boolean =>
  typeof status === "string" && status === "published"

const isArchived = (status?: ConfigurationCardPropsCardStatus): boolean =>
  typeof status === "boolean" && status === true

const isDraft = (status?: ConfigurationCardPropsCardStatus): boolean =>
  typeof status === "string" && status === "draft"

const countDownIcon =
  "https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/0300c07c-d7bb-4ced-ade3-5be9c1b2720b.svg"

const pencilIcon =
  "https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/38cffef3-17a8-496d-8bc4-134b09cc9aef.svg"

const trashIcon =
  "https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/a7335c35-a0c2-4674-9860-2e5db47ba334.svg"

// ─── Sub Components ───────────────────────────────────────────────────────────

interface StatBadgeProps {
  icon: string
  count: number
  label: string
}

const StatBadge: FC<StatBadgeProps> = ({ icon, count, label }) => (
  <EmployeeDutyShiftsWrapper
    image={icon}
    count={count}
    plan={label}
    status={label}
  />
)

interface CardHeaderProps {
  date: string
  status?: ConfigurationCardPropsCardStatus
  onToggle?: () => void
}

const CardHeader: FC<CardHeaderProps> = ({ date, status, onToggle }) => (
  <div className="flex justify-between items-center">
    <p className="text-slate-500 text-xs">{date}</p>
    {isArchived(status) ? (
      <small className="whitespace-nowrap bg-slate-200 text-slate-500 text-[12px] w-20 py-[2px] px-2 rounded-full flex justify-center items-center">
        Archive plan
      </small>
    ) : (
      <Switch
        checked={isPublished(status)}
        onChange={onToggle}
        checkedChildren={
          <span style={{ color: "#FFFFFF", fontSize: 12 }}>Active</span>
        }
        unCheckedChildren={
          <span style={{ color: "#0F172A", fontSize: 12 }}>Draft</span>
        }
        style={{
          backgroundColor: isPublished(status) ? "#004AF5" : "#E2E8F0",
          ["--handle-icon" as string]: `url(${powerIcon})`,
        }}
        className="[&_.ant-switch-handle::before]:!bg-[image:var(--handle-icon)] [&_.ant-switch-handle::before]:!bg-no-repeat [&_.ant-switch-handle::before]:!bg-center"
      />
    )}
  </div>
)

interface CardActionsProps {
  status?: ConfigurationCardPropsCardStatus
  isLoading?: boolean
  isRestoreLoading?: boolean
  pencilIcon: string
  countDownIcon: string
  onEdit?: () => void
  onArchive?: () => void
  onRestore?: () => void
  onDelete?: () => void
  isDeletingShiftPlan?: boolean
}

const CardActions: FC<CardActionsProps> = ({
  status,
  isLoading,
  onDelete,
  isRestoreLoading,
  pencilIcon,
  countDownIcon,
  onEdit,
  onArchive,
  onRestore,
  isDeletingShiftPlan,
}) => {
  // Draft — only edit
  if (isDraft(status)) {
    return (
      <div className="flex items-center">
        <CustomButtonWrapper
          onClick={onEdit!}
          image={pencilIcon}
          buttonText="Edit"
        />
        <DividerVertical />
        <CustomButtonWrapper
          onClick={onArchive!}
          image={countDownIcon}
          buttonText="Archive"
          isLoading={isLoading}
        />
        <DividerVertical />
        <CustomButtonWrapper
          onClick={onDelete!}
          image={trashIcon}
          buttonText="Delete"
          isLoading={isDeletingShiftPlan}
        />
      </div>
    )
  }

  // Archived — only restore
  if (isArchived(status)) {
    return (
      <div className="flex items-center">
        <CustomButtonWrapper
          onClick={onRestore!}
          image={countDownIcon}
          buttonText="Restore from archive"
          isLoading={isRestoreLoading}
        />
        <DividerVertical />
        <CustomButtonWrapper
          onClick={onDelete!}
          image={trashIcon}
          buttonText="Delete"
          isLoading={isDeletingShiftPlan}
        />
      </div>
    )
  }

  // Active (false boolean) — edit + archive
  if (isPublished(status)) {
    return (
      <div className="flex items-center">
        <CustomButtonWrapper
          onClick={onEdit!}
          image={pencilIcon}
          buttonText="Edit"
        />
        <DividerVertical />
        <CustomButtonWrapper
          onClick={onArchive!}
          image={countDownIcon}
          buttonText="Archive"
          isLoading={isLoading}
        />
        <DividerVertical />
        <CustomButtonWrapper
          onClick={onDelete!}
          image={trashIcon}
          buttonText="Delete"
          isLoading={isDeletingShiftPlan}
        />
      </div>
    )
  }

  return null
}

// ─── Main Component ───────────────────────────────────────────────────────────

const ConfigurationCard: FC<ConfigurationCardProps> = ({
  date,
  title,
  employeeCount,
  shiftCount,
  status,
  isLoading,
  isRestoreLoading,
  onEdit,
  onArchive,
  onRestore,
  onToggle,
  onDelete,
  isDeletingShiftPlan,
  flaggedForDeletion,
}) => (
  <div
    className={`${
      flaggedForDeletion ? "border-[#FF4D4F] border-2" : "border-[#E2E8F0]"
    } border rounded-lg w-full min-h-[10rem] flex flex-col relative overflow-hidden`}
  >
    {/* Top Banner Gradient Overlay */}
    {flaggedForDeletion && (
      <div className="absolute top-0 left-0 right-0 h-16 bg-gradient-to-b from-red-200 to-transparent opacity-60 pointer-events-none" />
    )}

    {/* Archive Banner */}

    <div className="p-4 flex flex-col flex-1">
      <div className="flex flex-col gap-3">
        {/* Header */}
        {flaggedForDeletion ? (
          <div className="text-[#E7000B] font-bold text-[12px] flex items-center gap-2">
            <img
              src={
                "https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/1423a23a-b056-41fa-9b42-82343018c203.png"
              }
              width={16}
              height={16}
            />
            <span>Scheduled for deletion</span>
          </div>
        ) : (
          <CardHeader
            date={date}
            status={status}
            onToggle={onToggle}
          />
        )}

        {/* Title */}
        <div className="flex items-center">
          <p className="font-bold text-sm text-slate-900 capitalize">{title}</p>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-2">
          <StatBadge
            icon={userIcon}
            count={employeeCount}
            label="employee"
          />
          <StatBadge
            icon={alarmIcon}
            count={shiftCount}
            label="shift period"
          />
        </div>
      </div>

      <div className="mt-auto">
        <Divider
          className="text-[#CAD5E2]"
          style={{ margin: "10px 0px" }}
        />

        {/* Actions */}
        <CardActions
          status={status}
          isLoading={isLoading}
          isRestoreLoading={isRestoreLoading}
          pencilIcon={pencilIcon}
          countDownIcon={countDownIcon}
          onEdit={onEdit}
          onArchive={onArchive}
          onRestore={onRestore}
          onDelete={onDelete}
          isDeletingShiftPlan={isDeletingShiftPlan}
        />
      </div>
    </div>
  </div>
)

export default ConfigurationCard

const CustomButtonWrapper = ({
  onClick,
  image,
  buttonText,
  isLoading,
}: {
  onClick: () => void
  image: string
  buttonText: string
  isLoading?: boolean
}) => {
  return (
    <div
      onClick={onClick}
      className="flex items-center gap-1 px-2 h-9 rounded-md cursor-pointer hover:bg-slate-100"
    >
      <img
        width={14}
        height={14}
        src={image}
        alt={buttonText}
        className="w-3.5 h-3.5"
      />
      <Button
        ghost
        type="text"
        loading={isLoading}
        className="!p-0 !h-auto hidden sm:flex items-center text-sm font-semibold !text-[#0F172B] hover:!bg-transparent"
      >
        {buttonText}
      </Button>
    </div>
  )
}
